import React, { useState, useEffect } from "react";
import {
  Container,
  Title,
  Select,
  Loader,
  Button,
  Avatar,
  Modal,
  TextInput,
  Group
} from "@mantine/core";
import { getSources, getSubcollectionsForSource } from "../utils/firebase";
import Edit from "../components/edit"; // ✅ Fixed lowercase import

// Use your provided EMPTY_TEMPLATES
const EMPTY_TEMPLATES = {
  facebook: {
    accessToken: "",
    adAccounts: [{ adAccountId: "", fetchedTill: "" }]
  },
  paypro: { key: "", vendor: "" },
  stripe: { key: "" }
};

const Dashboard = () => {
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [subcollections, setSubcollections] = useState({});
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newEntityModalOpen, setNewEntityModalOpen] = useState(false);
  const [newEntityName, setNewEntityName] = useState("");

  // ✅ Function to refresh sources & entities
  const refreshSources = async () => {
    setLoading(true);
    const fetchedSources = await getSources();
    setSources(fetchedSources);

    if (fetchedSources.length > 0) {
      const firstSource = fetchedSources[0].id;
      setSelectedSource(firstSource);

      // Fetch subcollections for the first source
      const fetchedSubcollections = await getSubcollectionsForSource(
        firstSource
      );
      setSubcollections(fetchedSubcollections);

      // Set first subcollection (if available)
      if (Object.keys(fetchedSubcollections).length > 0) {
        setSelectedCollection(Object.keys(fetchedSubcollections)[0]);
      }
    }

    setLoading(false);
  };

  // Fetch all sources
  useEffect(() => {
    refreshSources();
  }, []);

  // Fetch subcollections when source changes
  useEffect(() => {
    if (!selectedSource) return;

    setSelectedDocument(null); // Hide edit panel when source changes

    const fetchSubcollections = async () => {
      setLoading(true);
      const fetchedSubcollections = await getSubcollectionsForSource(
        selectedSource
      );
      setSubcollections(fetchedSubcollections);
      if (Object.keys(fetchedSubcollections).length > 0) {
        setSelectedCollection(Object.keys(fetchedSubcollections)[0]); // Set first entity
      }
      setLoading(false);
    };

    fetchSubcollections();
  }, [selectedSource]);

  // Fetch documents when collection (entity) is selected
  useEffect(() => {
    if (
      !selectedCollection ||
      !selectedSource ||
      selectedCollection === "Add New"
    )
      return;

    setSelectedDocument(null); // Hide edit panel when entity changes
    setDocuments(
      (subcollections[selectedCollection] || []).map((doc) => ({
        ...doc,
        collection: selectedCollection,
        source: selectedSource
      }))
    );
  }, [selectedCollection, subcollections]);

  // Handle "Add New" selection
  const handleAddNewEntity = () => {
    setNewEntityModalOpen(true); // Open modal for new entity name input
  };

  // Create new entity after modal input
  const createNewEntity = () => {
    if (!newEntityName || !selectedSource) return;

    const emptyDoc = {
      id: "settings", // ✅ Always use "settings" as the document name
      collection: newEntityName,
      source: selectedSource,
      fields: EMPTY_TEMPLATES[selectedSource] || {}
    };

    setSelectedDocument(emptyDoc);
    setNewEntityModalOpen(false);
    setNewEntityName(""); // Reset input field
  };

  return (
    <Container>
      <Title order={1} mb="md">
        Dashboard v0.1 (Feb 5, 2025)
      </Title>

      {/* Source Selector */}
      <Select
        label="Select Source"
        placeholder="Choose a source"
        data={sources.map((source) => ({ value: source.id, label: source.id }))}
        value={selectedSource}
        onChange={setSelectedSource}
        mb="md"
      />

      {/* Entity (Subcollection) Selector */}
      <Select
        label="Select Entity"
        placeholder="Choose an entity"
        data={[
          ...Object.keys(subcollections).map((collection) => ({
            value: collection,
            label: collection
          })),
          { value: "Add New", label: "➕ Add New" }
        ]}
        value={selectedCollection}
        onChange={(val) => {
          if (val === "Add New") {
            handleAddNewEntity();
          } else {
            setSelectedCollection(val);
          }
        }}
        mb="md"
        disabled={!selectedSource}
      />

      {loading && <Loader size="lg" />}

      {selectedCollection &&
        selectedCollection !== "Add New" &&
        documents.length > 0 && (
          <Container mt="lg">
            <Title order={2}>{selectedCollection}</Title>
            {documents.map((doc) => (
              <Button
                key={doc.id}
                fullWidth
                variant="light"
                onClick={() => setSelectedDocument(doc)}
                mt="sm">
                {doc.id}
              </Button>
            ))}
          </Container>
        )}

      {selectedDocument && (
        <Edit
          selectedDocument={selectedDocument}
          setSelectedDocument={setSelectedDocument}
          refreshSources={refreshSources}
        />
      )}

      <Modal
        opened={newEntityModalOpen}
        onClose={() => setNewEntityModalOpen(false)}
        title={`Enter new entity name for ${selectedSource}`}
        centered
        size="md">
        <TextInput
          label="Entity Name"
          placeholder="Enter name"
          value={newEntityName}
          onChange={(e) => setNewEntityName(e.target.value)}
        />
        <Group position="center" mt="md">
          <Button onClick={createNewEntity} color="green">
            Create
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};

export default Dashboard;
